<script setup lang="ts">
import { Check, ChevronDown } from "lucide-vue-next";

type SelectOption = {
  label: string;
  value: string;
  prefix?: string;
  prefixSrc?: string;
};

const props = defineProps<{
  modelValue?: string;
  options: SelectOption[];
  placeholder?: string;
  selectedText?: string;
  align?: "left" | "right";
  width?: "auto" | "sm" | "md" | "lg" | "full";
  triggerClass?: string;
  openTriggerClass?: string;
  valueClass?: string;
  placeholderClass?: string;
  sheetTitle?: string;
  optionClass?: string;
  listClass?: string;
  optionVariant?: "default" | "radio";
  hideChevron?: boolean;
  error?: string | boolean;
}>();

const emit = defineEmits<{
  "update:modelValue": [value: string];
}>();

const selectRef = useTemplateRef<HTMLElement>("selectRef");
const desktopPanelRef = useTemplateRef<HTMLElement>("desktopPanelRef");
const mobilePanelRef = useTemplateRef<HTMLElement>("mobilePanelRef");

const isOpen = ref(false);

let previousBodyOverflow = "";

const selectedOption = computed(() => {
  return props.options.find(option => option.value === props.modelValue);
});

const panelClasses = computed(() => [
  props.align === "right" ? "right-0" : "left-0",
  {
    auto: "min-w-[140px]",
    sm: "w-[160px]",
    md: "w-[220px]",
    lg: "w-[300px]",
    full: "w-full",
  }[props.width || "full"],
]);

const triggerClasses = computed(() => {
  if (props.triggerClass) return props.triggerClass;

  return [
    "flex w-full items-center justify-between gap-2 rounded-lg px-3 py-2 text-left text-sm font-semibold transition duration-300",
    isOpen.value
      ? "bg-primary-100 text-primary"
      : "text-[#181818] hover:bg-primary-50 hover:text-primary",
  ];
});

const triggerText = computed(() => {
  return (
    props.selectedText ||
    selectedOption.value?.label ||
    props.placeholder ||
    "Select"
  );
});

function isMobileViewport() {
  if (typeof window === "undefined") return false;

  return window.matchMedia("(max-width: 767px)").matches;
}

function lockBodyScroll() {
  if (typeof document === "undefined") return;
  if (!isMobileViewport()) return;

  previousBodyOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";
}

function unlockBodyScroll() {
  if (typeof document === "undefined") return;
  if (!isMobileViewport()) return;

  document.body.style.overflow = previousBodyOverflow;
}

function toggleSelect() {
  isOpen.value = !isOpen.value;
}

function closeSelect() {
  isOpen.value = false;
}

function selectOption(value: string) {
  emit("update:modelValue", value);
  closeSelect();
}

function stopScrollEvent() {}

function handleEscPress(event: KeyboardEvent) {
  if (event.key === "Escape") {
    closeSelect();
  }
}

function handlePointerDown(event: PointerEvent) {
  if (!isOpen.value) return;

  const target = event.target as Node;

  if (selectRef.value?.contains(target)) return;
  if (desktopPanelRef.value?.contains(target)) return;
  if (mobilePanelRef.value?.contains(target)) return;

  closeSelect();
}

watch(isOpen, value => {
  if (!isMobileViewport()) return;

  if (value) {
    lockBodyScroll();
  } else {
    unlockBodyScroll();
  }
});

onMounted(() => {
  window.addEventListener("keyup", handleEscPress);
  document.addEventListener("pointerdown", handlePointerDown);
});

onBeforeUnmount(() => {
  window.removeEventListener("keyup", handleEscPress);
  document.removeEventListener("pointerdown", handlePointerDown);
  unlockBodyScroll();
});
</script>

<template>
  <div
    ref="selectRef"
    class="relative inline-flex w-full"
    :class="{ 'z-[1030]': isOpen }"
  >
    <button
      type="button"
      :aria-invalid="Boolean(error)"
      :class="[
        triggerClasses,
        isOpen && openTriggerClass ? openTriggerClass : '',
        error
          ? '!border-app-danger focus-visible:!border-app-danger focus-visible:!ring-app-danger/10'
          : '',
      ]"
      @click="toggleSelect"
    >
      <slot name="icon" />

      <img
        v-if="selectedOption?.prefixSrc"
        :src="selectedOption.prefixSrc"
        alt=""
        class="h-[15px] w-[22px] shrink-0 rounded-[3px] object-cover shadow-[0_0_0_1px_rgba(0,0,0,0.08)]"
        aria-hidden="true"
      />

      <span
        v-else-if="selectedOption?.prefix"
        class="flex size-5 shrink-0 items-center justify-center overflow-hidden rounded-full text-base leading-none"
        aria-hidden="true"
      >
        {{ selectedOption.prefix }}
      </span>

      <span
        class="min-w-0 flex-1 truncate"
        :class="
          isOpen
            ? 'text-primary'
            : selectedOption
              ? valueClass || 'text-[#181818]'
              : placeholderClass || 'text-[#777]'
        "
      >
        {{ triggerText }}
      </span>

      <ChevronDown
        v-if="!hideChevron"
        class="h-4 w-4 shrink-0 transition-transform duration-300"
        :class="{ 'rotate-180': isOpen }"
      />
    </button>

    <!-- Desktop dropdown -->
    <Transition name="select-fade">
      <div
        v-if="isOpen"
        ref="desktopPanelRef"
        class="absolute top-[calc(100%+8px)] z-[1040] hidden overflow-hidden rounded-xl border border-app-border bg-white p-0 shadow-xl md:block"
        :class="panelClasses"
        data-scroll-lock-ignore
      >
        <ul
          class="app-select-scroll flex max-h-[220px] flex-col divide-y divide-app-border overflow-y-scroll overscroll-contain"
          :class="listClass"
          data-scroll-lock-ignore
          @wheel.stop="stopScrollEvent"
          @touchmove.stop="stopScrollEvent"
        >
          <li v-for="option in options" :key="option.value">
            <button
              type="button"
              class="flex w-full items-center justify-between gap-3 px-4 py-3 text-sm font-semibold transition duration-300"
              :class="[
                modelValue === option.value
                  ? optionVariant === 'radio'
                    ? 'bg-white text-app-text'
                    : 'bg-primary-100 text-primary'
                  : 'text-[#181818] hover:bg-primary-50 hover:text-primary',
                optionClass,
              ]"
              @click="selectOption(option.value)"
            >
              <span class="flex min-w-0 items-center gap-2.5">
                <img
                  v-if="option.prefixSrc"
                  :src="option.prefixSrc"
                  alt=""
                  class="h-[15px] w-[22px] shrink-0 rounded-[3px] object-cover shadow-[0_0_0_1px_rgba(0,0,0,0.08)]"
                  aria-hidden="true"
                />
                <span
                  v-else-if="option.prefix"
                  class="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-50 text-lg leading-none ring-1 ring-black/5"
                  aria-hidden="true"
                >
                  {{ option.prefix }}
                </span>
                <span class="truncate">
                  {{ option.label }}
                </span>
              </span>

              <span
                v-if="optionVariant === 'radio'"
                class="flex size-5 shrink-0 items-center justify-center rounded-full border transition duration-300"
                :class="
                  modelValue === option.value
                    ? 'border-primary bg-primary'
                    : 'border-[#7B8389] bg-white'
                "
                aria-hidden="true"
              >
                <span
                  v-if="modelValue === option.value"
                  class="size-1.5 rounded-full bg-white"
                />
              </span>

              <Check
                v-else-if="modelValue === option.value"
                class="h-4 w-4 shrink-0 text-primary"
              />
            </button>
          </li>
        </ul>
      </div>
    </Transition>

    <!-- Mobile sheet -->
    <Teleport to="body">
      <Transition name="select-backdrop">
        <button
          v-if="isOpen"
          type="button"
          class="fixed inset-0 z-[1030] bg-black/40 md:hidden"
          aria-label="Close select"
          @click="closeSelect"
        />
      </Transition>

      <Transition name="select-sheet">
        <div
          v-if="isOpen"
          ref="mobilePanelRef"
          class="fixed inset-x-0 bottom-0 z-[1040] max-h-[42dvh] overflow-hidden rounded-t-2xl border border-app-border bg-white p-3 shadow-2xl md:hidden"
          data-scroll-lock-ignore
        >
          <div class="mx-auto mb-3 h-1 w-10 rounded-full bg-[#D1D5DB]" />

          <div
            v-if="sheetTitle"
            class="mb-2 px-1 text-base font-semibold text-[#181818]"
          >
            {{ sheetTitle }}
          </div>

          <ul
            class="app-select-scroll flex max-h-[calc(42dvh-36px)] flex-col divide-y divide-app-border overflow-y-auto overscroll-contain"
            :class="listClass"
            data-scroll-lock-ignore
            @wheel.stop="stopScrollEvent"
            @touchmove.stop="stopScrollEvent"
          >
            <li v-for="option in options" :key="option.value">
              <button
                type="button"
                class="flex w-full items-center justify-between gap-3 px-3 py-3 text-base font-semibold transition duration-300"
                :class="[
                  modelValue === option.value
                    ? optionVariant === 'radio'
                      ? 'bg-white text-app-text'
                      : 'bg-primary-100 text-primary'
                    : 'text-[#181818] hover:bg-primary-50 hover:text-primary',
                  optionClass,
                ]"
                @click="selectOption(option.value)"
              >
                <span class="flex min-w-0 items-center gap-2.5">
                  <img
                    v-if="option.prefixSrc"
                    :src="option.prefixSrc"
                    alt=""
                    class="h-[15px] w-[22px] shrink-0 rounded-[3px] object-cover shadow-[0_0_0_1px_rgba(0,0,0,0.08)]"
                    aria-hidden="true"
                  />
                  <span
                    v-else-if="option.prefix"
                    class="flex size-6 shrink-0 items-center justify-center overflow-hidden rounded-full bg-gray-50 text-lg leading-none ring-1 ring-black/5"
                    aria-hidden="true"
                  >
                    {{ option.prefix }}
                  </span>
                  <span class="truncate">
                    {{ option.label }}
                  </span>
                </span>

                <span
                  v-if="optionVariant === 'radio'"
                  class="flex size-5 shrink-0 items-center justify-center rounded-full border transition duration-300"
                  :class="
                    modelValue === option.value
                      ? 'border-primary bg-primary'
                      : 'border-[#7B8389] bg-white'
                  "
                  aria-hidden="true"
                >
                  <span
                    v-if="modelValue === option.value"
                    class="size-1.5 rounded-full bg-white"
                  />
                </span>

                <Check
                  v-else-if="modelValue === option.value"
                  class="h-4 w-4 shrink-0 text-primary"
                />
              </button>
            </li>
          </ul>
        </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.select-fade-enter-active,
.select-fade-leave-active {
  transition:
    opacity 140ms ease,
    transform 140ms ease;
}

.select-fade-enter-from,
.select-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px) scale(0.98);
}

.select-backdrop-enter-active,
.select-backdrop-leave-active {
  transition: opacity 160ms ease;
}

.select-backdrop-enter-from,
.select-backdrop-leave-to {
  opacity: 0;
}

.select-sheet-enter-active,
.select-sheet-leave-active {
  transition:
    opacity 180ms ease,
    transform 180ms ease;
}

.select-sheet-enter-from,
.select-sheet-leave-to {
  opacity: 0;
  transform: translateY(100%);
}

.app-select-scroll {
  scrollbar-width: thin;
  scrollbar-color: #aeb8bf #f4f6f7;
  scrollbar-gutter: stable;
}

.app-select-scroll::-webkit-scrollbar {
  height: 8px;
  width: 9px;
}

.app-select-scroll::-webkit-scrollbar-thumb {
  border-radius: 9999px;
  border: 2px solid #f4f6f7;
  background: #aeb8bf;
}

.app-select-scroll::-webkit-scrollbar-track {
  background: #f4f6f7;
}
</style>
